import React, { useState, useEffect } from 'react';
import {
  shouldShowConsentBanner,
  acceptAllConsent,
  getConsent,
} from '../utils/cookieConsent';

/**
 * Cookie Consent Banner Component
 * Displays on first visit and provides options to accept all or manage preferences
 * Accessible, mobile-responsive, and styled to match Book A Therapy brand
 */
const CookieConsentBanner = ({ onOpenPreferences }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if banner should be shown
    if (shouldShowConsentBanner()) {
      // Small delay for smooth entrance animation
      setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, 500);
    }

    // Listen for consent changes from other tabs/windows
    const handleStorageChange = () => {
      if (!shouldShowConsentBanner()) {
        handleClose();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('consentUpdated', handleClose);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('consentUpdated', handleClose);
    };
  }, []);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  const handleAcceptAll = () => {
    acceptAllConsent();
    handleClose();
  };

  const handleManagePreferences = () => {
    if (onOpenPreferences) {
      onOpenPreferences();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      aria-modal="false"
      className={`fixed bottom-6 left-6 right-6 sm:left-auto sm:right-6 sm:max-w-md z-[9999] transform transition-all duration-500 ease-out ${
        isAnimating ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'
      }`}
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Decorative top bar */}
        <div className="h-1.5 bg-gradient-to-r from-[#252F4F] via-[#98C454] to-[#252F4F]" />

        <div className="p-6">
          {/* Icon and title */}
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#98C454]/10 to-[#252F4F]/10 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-[#252F4F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <div className="flex-1">
              <h2
                id="cookie-banner-title"
                className="text-lg font-bold text-[#252F4F] mb-1"
              >
                Cookie Settings
              </h2>
              <p
                id="cookie-banner-description"
                className="text-sm text-gray-600 leading-relaxed"
              >
                We use cookies to give you the best experience.{' '}
                <a
                  href="/privacy-policy"
                  className="text-[#252F4F] font-medium hover:text-[#98C454] underline transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2">
            <button
              onClick={handleAcceptAll}
              className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-[#252F4F] rounded-xl hover:bg-[#1a2136] active:scale-[0.98] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#252F4F] focus:ring-offset-2 shadow-lg shadow-[#252F4F]/20"
              aria-label="Accept all cookies"
            >
              Accept All
            </button>

            <button
              onClick={handleManagePreferences}
              className="w-full px-4 py-2.5 text-sm font-medium text-[#252F4F] bg-gray-50 rounded-xl hover:bg-gray-100 active:scale-[0.98] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#252F4F] focus:ring-offset-2"
              aria-label="Manage cookie preferences"
            >
              Customize
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
